import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '@/components/hydlib/PublicLayout';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = "'Space Mono', monospace";
const serifDisplay = "'Libre Caslon Display', serif";

const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const labelStyle = { display: 'block', fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--a1)', marginBottom: 6 };
const inputStyle = { width: '100%', boxSizing: 'border-box', border: '1px solid var(--line)', background: 'var(--bg)', padding: '11px 14px', fontSize: 15, color: 'var(--ink)', fontFamily: 'inherit' };
const cardStyle = (accent) => ({ position: 'relative', border: '1px solid var(--line)', borderTop: `6px solid var(${accent})`, background: 'var(--paper)', padding: '28px 30px', marginBottom: 28 });
const monoBtn = (bg, fg) => ({ cursor: 'pointer', border: '1px solid var(--line)', background: bg, color: fg, fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '9px 18px' });
const modalOverlay = { position: 'fixed', inset: 0, background: 'color-mix(in oklch, var(--ink) 55%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 };
const modalBox = { background: 'var(--paper)', border: '1px solid var(--line)', boxShadow: '8px 9px 0 color-mix(in oklch, var(--line) 35%, transparent)', padding: '30px 32px', maxWidth: 480, width: '100%', maxHeight: '85vh', overflowY: 'auto' };

function Profile() {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', address: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({ name: user.name || '', email: user.email || '', phone: user.phone || '', address: user.address || '' });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await axios.put(`${API}/users/profile`, formData, { headers: getAuthHeader() });
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '', address: user?.address || '' });
    setIsEditing(false);
    setError('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (passwordForm.newPassword.length < 6) { setPasswordError('New password must be at least 6 characters'); return; }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { setPasswordError('New passwords do not match'); return; }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/users/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      }, { headers: getAuthHeader() });
      setPasswordSuccess('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => { setShowPasswordModal(false); setPasswordSuccess(''); }, 2000);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const isSubscriptionActive = user?.subscription_status === 'active';
  const isSubscriptionCancelled = user?.subscription_cancelled;
  const subscriptionEndDate = user?.subscription_end_date ? new Date(user.subscription_end_date) : null;
  const statusLabel = isSubscriptionActive
    ? (isSubscriptionCancelled ? 'Cancelled · active until end' : 'Active')
    : (user?.subscription_status || 'Not active');
  const statusColor = isSubscriptionActive ? (isSubscriptionCancelled ? 'var(--a4)' : 'var(--a3)') : 'var(--a4)';

  return (
    <PublicLayout theme="archive" grid="--a5" gridStrength={28}>
      <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a3) 0 5px, transparent 6px)' }} />

      <div className="hydlib-pad" style={{ position: 'relative', maxWidth: 860, margin: '0 auto', padding: '64px 48px', boxSizing: 'border-box' }}>
        <h1 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 48, margin: '0 0 8px' }} data-testid="profile-heading">
          <span style={{ background: 'var(--bg)', boxShadow: '8px 0 0 var(--bg), -8px 0 0 var(--bg)' }}>My profile</span>
        </h1>
        <p style={{ fontFamily: mono, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 40px' }}>
          <span style={{ background: 'var(--bg)', boxShadow: '6px 0 0 var(--bg), -6px 0 0 var(--bg)' }}>Your details, membership and pickups</span>
        </p>

        {success && <div style={{ border: '1px solid var(--line)', borderLeft: '6px solid var(--a3)', background: 'var(--paper)', padding: '12px 16px', marginBottom: 20, fontSize: 14.5 }} data-testid="profile-success">{success}</div>}
        {error && <div style={{ border: '1px solid var(--line)', borderLeft: '6px solid var(--a4)', background: 'var(--paper)', padding: '12px 16px', marginBottom: 20, fontSize: 14.5, color: 'var(--a4)' }} data-testid="profile-error">{error}</div>}

        {/* Personal details */}
        <div style={cardStyle('--a1')} data-testid="profile-details-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)' }}>Personal details</div>
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} data-testid="edit-profile-button" style={monoBtn('var(--paper)', 'var(--ink)')}>Edit →</button>
            )}
          </div>
          <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="hydlib-cols">
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Name</label>
              <input type="text" value={formData.name} disabled={!isEditing} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="profile-name-input" style={{ ...inputStyle, opacity: isEditing ? 1 : 0.7 }} required />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={formData.email} disabled data-testid="profile-email-input" style={{ ...inputStyle, opacity: 0.6 }} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input type="tel" value={formData.phone} disabled={!isEditing} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="profile-phone-input" style={{ ...inputStyle, opacity: isEditing ? 1 : 0.7 }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Default address</label>
              <textarea value={formData.address} disabled={!isEditing} rows={2} placeholder="Flat / street / area — used to pre-fill pickup & delivery forms" onChange={(e) => setFormData({ ...formData, address: e.target.value })} data-testid="profile-address-input" style={{ ...inputStyle, opacity: isEditing ? 1 : 0.7, resize: 'vertical' }} />
            </div>
            {isEditing && (
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10 }}>
                <button type="submit" disabled={saving} data-testid="save-profile-button" className="hydlib-highlight" style={{ cursor: saving ? 'wait' : 'pointer', border: 'none', fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: '10px 22px', background: 'linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)' }}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
                <button type="button" onClick={handleCancelEdit} style={monoBtn('var(--paper)', 'var(--ink)')}>Cancel</button>
              </div>
            )}
          </form>
        </div>

        {/* Membership */}
        <div style={cardStyle('--a3')} data-testid="profile-membership-card">
          <div style={{ fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a3)', marginBottom: 18 }}>Membership details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }} className="hydlib-cols">
            <div>
              <div style={labelStyle}>Status</div>
              <div style={{ fontFamily: serifDisplay, fontSize: 22, color: statusColor, textTransform: 'capitalize' }} data-testid="subscription-status">{statusLabel}</div>
            </div>
            <div>
              <div style={labelStyle}>Member since</div>
              <div style={{ fontFamily: serifDisplay, fontSize: 22 }}>{user?.created_at ? fmtDate(user.created_at) : '—'}</div>
            </div>
            {subscriptionEndDate && (
              <div>
                <div style={labelStyle}>Valid until</div>
                <div style={{ fontFamily: serifDisplay, fontSize: 22 }}>{fmtDate(subscriptionEndDate)}</div>
              </div>
            )}
          </div>
          {isSubscriptionCancelled && (
            <div style={{ marginTop: 18, border: '1px dashed var(--line)', background: 'var(--bg)', padding: '12px 16px', fontSize: 14.5 }}>
              <strong>Subscription cancelled:</strong> your access continues until {subscriptionEndDate ? fmtDate(subscriptionEndDate) : 'the end of the period'}. You can resubscribe anytime from My Library → Payments.
            </div>
          )}
          <div style={{ marginTop: 20, borderTop: '1px dashed var(--line)', paddingTop: 16, fontSize: 14.5 }}>
            Renewals, pauses, pickups and cancellation now live in{' '}
            <Link to="/my-library?section=payments" data-testid="manage-membership-link" style={{ color: 'var(--a3)', textDecoration: 'underline' }}>My Library → Payments</Link>.
          </div>
        </div>

        {/* Security */}
        <div style={cardStyle('--a5')} data-testid="profile-security-card">
          <div style={{ fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a5)', marginBottom: 14 }}>Security</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 15 }}>Password — last line of defence for your TBR pile.</span>
            <button
              onClick={() => { setShowPasswordModal(true); setPasswordError(''); setPasswordSuccess(''); setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); }}
              data-testid="change-password-button"
              style={monoBtn('var(--ink)', 'var(--paper)')}
            >
              Change password
            </button>
          </div>
        </div>

        {/* Pickups */}
      </div>

      {/* Change password modal */}
      {showPasswordModal && (
        <div style={modalOverlay} onClick={() => setShowPasswordModal(false)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()} data-testid="password-modal">
            <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 26, margin: '0 0 18px' }}>Change password</h3>
            {passwordSuccess ? (
              <div style={{ border: '1px solid var(--line)', borderLeft: '6px solid var(--a3)', background: 'var(--bg)', padding: '12px 16px', fontSize: 14.5 }} data-testid="password-success">{passwordSuccess}</div>
            ) : (
              <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Current password</label>
                  <input type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} style={inputStyle} required data-testid="current-password-input" />
                </div>
                <div>
                  <label style={labelStyle}>New password</label>
                  <input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} style={inputStyle} required minLength={6} data-testid="new-password-input" />
                </div>
                <div>
                  <label style={labelStyle}>Confirm new password</label>
                  <input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} style={inputStyle} required data-testid="confirm-password-input" />
                </div>
                {passwordError && <div style={{ color: 'var(--a4)', fontSize: 14 }} data-testid="password-error">{passwordError}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                  <button type="button" onClick={() => setShowPasswordModal(false)} disabled={changingPassword} style={{ ...monoBtn('var(--paper)', 'var(--ink)'), flex: 1 }}>Cancel</button>
                  <button type="submit" disabled={changingPassword} data-testid="submit-password-change" style={{ ...monoBtn('var(--ink)', 'var(--paper)'), flex: 1, opacity: changingPassword ? 0.7 : 1 }}>
                    {changingPassword ? 'Changing…' : 'Change password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </PublicLayout>
  );
}

export default Profile;
